import { View } from '@play-co/timestep-core/ui';
import { user } from 'src/StateObserver';
import { SearchInputField } from 'src/game/components/SearchInputField';
import Scroll from 'src/game/components/shared/Scroll';
import i18n from 'src/lib/i18n/i18n';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import {
  getFriendshipPendingRequests,
  getFriendshipPendingRequestsSent,
} from 'src/replicant/friends/friends.getters';
import { FriendshipRequest } from 'src/replicant/friends/friends.ruleset';
import { acceptFriendRequest, rejectFriendRequest } from '../friends.sequences';
import { FriendItemParams, FriendItem } from './FriendItem';

export class FriendRequestTab {
  private tab: View;
  private search: SearchInputField;
  private scroll: Scroll<FriendItemParams, FriendItem>;
  private message: LangBitmapFontTextView;
  private filter: string = '';

  constructor(tab: View) {
    this.tab = tab;

    this.search = new SearchInputField({
      superview: tab,
      placeholder: i18n('friends.filterPlaceholder'),
      x: 0,
      y: 45,
      inputClass: 'popupFriendsFilter',
      width: tab.style.width,
      height: 64,
      allowClear: true,
      onChange: (valid) => {
        if (!valid) return;
        this.filter = this.search.text;
        this.refresh();
      },
    });
    this.search.inputField.attachToPopup('popupFriends');

    this.scroll = new Scroll({
      superview: tab,
      recycle: true,
      createItem: (superview: View, i: number, data: FriendItemParams) => {
        const item = new FriendItem({ superview });
        item.setup(this.getItemData(data));
        return item;
      },
      setData: (i: number, data: FriendItemParams, item: FriendItem) => {
        item.setup(this.getItemData(data));
      },
      showBg: true,
      rect: {
        x: 0,
        y: 80,
        width: tab.style.width,
        height: tab.style.height - 80,
      },
    });

    this.message = new LangBitmapFontTextView({
      superview: tab,
      align: 'center',
      verticalAlign: 'center',
      size: 28,
      font: 'Body',
      width: tab.style.width,
      height: 200,
      color: '#AE7E4A',
      centerOnOrigin: true,
      x: tab.style.width / 2,
      y: tab.style.height / 2,
    });
  }

  private getItemData(params: FriendItemParams) {
    return {
      ...params,
      onAccept: async () => {
        const request = user().friendshipRequests[
          params.id
        ] as FriendshipRequest;
        // Do nothing if the request is not found
        if (!request) return;
        const ok = await acceptFriendRequest(request, 'hud');
        if (ok) this.refresh();
      },
      onReject: async () => {
        const request = user().friendshipRequests[
          params.id
        ] as FriendshipRequest;
        // Do nothing if the request is not found
        if (!request) return;
        const ok = await rejectFriendRequest(request, 'hud');
        if (ok) this.refresh();
      },
    };
  }

  public refresh() {
    const requestsReceived = getFriendshipPendingRequests(user())
      .filter((request) => {
        if (!this.filter) return true;
        const name = request.owner.name.toLowerCase();
        const filter = this.filter.toLowerCase();
        return name.includes(filter);
      })
      .map((request) => {
        return {
          id: request.owner.id,
          name: request.owner.name,
          profilePicture: request.owner.profilePicture,
          species: request.owner.species,
        } as FriendItemParams;
      });

    const requestsSent = getFriendshipPendingRequestsSent(user())
      .filter((request) => {
        if (!this.filter) return true;
        const name = request.target.name.toLowerCase();
        const filter = this.filter.toLowerCase();
        return name.includes(filter);
      })
      .map((request) => {
        return {
          id: request.target.id,
          name: request.target.name,
          profilePicture: request.target.profilePicture,
          species: request.target.species,
          requestSent: true,
        } as FriendItemParams;
      });

    const items = [...requestsReceived, ...requestsSent];

    this.scroll.setItems(items);
    this.message.updateOpts({
      visible: items.length === 0,
    });

    if (!this.filter && items.length === 0) {
      this.message.localeText = () => i18n('friends.noRequests');
    }

    if (this.filter && items.length === 0) {
      this.message.localeText = () => i18n('friends.noResults');
    }
  }
}
